// =====================================================================
// paymentPlanBreakdown — turn a chosen billing model into the plain
// peso lines the patient/staff read ("What the patient pays").
//
// This is the single source of truth for the *display* of a plan's
// money split. It does NOT decide what is actually collected — the
// database (get_plan_billing_state) owns that. It mirrors the same
// rules so the on-screen breakdown and the live checkout suggestion
// agree:
//
//   full       → whole total on visit 1.
//   50_50      → half on visit 1, half on completion.
//   per_phase  → total split evenly across the visits.
//   custom     → down payment now; then either flexible-per-visit with
//                the balance due on the final visit, OR (when a
//                num_payments count is set) the balance-after-DP split
//                evenly into that many payments. An optional per-visit
//                minimum shows as a soft floor note.
//
// All amounts are centavos in, pesos out. Splits floor and bank the
// remainder on the last visit so the lines sum to the total exactly.
// =====================================================================

import { computeScheduleAmounts } from "./paymentSchedule";

const CANONICAL = new Set(["per_phase", "50_50", "full", "custom"]);

export function formatPesos(centavos) {
  const n = Number(centavos);
  if (!Number.isFinite(n)) return "—";
  return `₱${Math.round(n / 100).toLocaleString("en-PH")}`;
}

// Even split of `total` centavos into `count` parts, remainder banked on
// the last part so the parts sum to `total` exactly.
function splitEven(total, count) {
  const t = Math.max(0, Math.round(Number(total) || 0));
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n <= 0) return [];
  const base = Math.floor(t / n);
  const out = new Array(n).fill(base);
  out[n - 1] = t - base * (n - 1);
  return out;
}

// A line: { key, label, value (centavos) | null, text (string) | null, tone }
// tone: 'normal' | 'muted' | 'accent'
function line(key, label, value, tone = "normal", text = null) {
  return { key, label, value: value == null ? null : Math.round(value), text, tone };
}

/**
 * Describe a payment plan as display lines.
 *
 * @param {object} p
 * @param {string} p.preset                 per_phase | 50_50 | full | custom
 * @param {number} p.totalCentavos          plan total (centavos)
 * @param {number} [p.visitCount]           number of visits/phases (>=1)
 * @param {number} [p.dpCentavos]           custom down payment (centavos)
 * @param {number} [p.minPerVisitCentavos]  custom soft floor per visit
 * @param {number} [p.numPayments]          custom installments for the balance-after-DP
 * @returns {{ ready: boolean, lines: Array, footnote: string|null }}
 */
export function describePaymentPlan({
  preset,
  totalCentavos,
  visitCount,
  dpCentavos,
  minPerVisitCentavos,
  numPayments,
} = {}) {
  const total = Math.max(0, Math.round(Number(totalCentavos) || 0));
  const visits = Math.max(1, Math.floor(Number(visitCount) || 1));

  if (!CANONICAL.has(preset)) {
    return { ready: false, lines: [], footnote: null };
  }
  if (total <= 0) {
    return { ready: false, lines: [], footnote: null };
  }

  if (preset === "full") {
    return {
      ready: true,
      footnote: null,
      lines: [line("full", "Whole amount on visit 1", total, "accent")],
    };
  }

  if (preset === "50_50") {
    const amts = computeScheduleAmounts(total, Math.max(2, visits), "50_50");
    const first = amts[0];
    const last = amts[amts.length - 1];
    return {
      ready: true,
      footnote:
        visits > 2
          ? "Nothing due on the visits in between — pay any time to lower the final balance."
          : null,
      lines: [
        line("first", "First visit", first, "normal"),
        line("final", "Upon completion", last, "accent"),
      ],
    };
  }

  if (preset === "per_phase") {
    const amts = computeScheduleAmounts(total, visits, "per_phase");
    if (visits <= 1) {
      return { ready: true, footnote: null, lines: [line("v1", "This visit", total, "accent")] };
    }
    const allEqual = amts.every((a) => a === amts[0]);
    if (visits > 6 || allEqual) {
      // Summarise so the card doesn't sprout a dozen rows.
      const last = amts[amts.length - 1];
      const evenNote =
        last !== amts[0]
          ? `Last visit ${formatPesos(last)} to round out the total.`
          : null;
      return {
        ready: true,
        footnote: evenNote,
        lines: [line("even", `Each of ${visits} visits`, amts[0], "accent")],
      };
    }
    return {
      ready: true,
      footnote: null,
      lines: amts.map((a, i) =>
        line(`v${i + 1}`, `Visit ${i + 1}`, a, i === amts.length - 1 ? "accent" : "normal"),
      ),
    };
  }

  // ---- custom (Flexible) ----------------------------------------------
  const dp = Math.max(0, Math.round(Number(dpCentavos) || 0));
  const min = Math.max(0, Math.round(Number(minPerVisitCentavos) || 0));
  const payments = Math.max(0, Math.floor(Number(numPayments) || 0));
  const remaining = Math.max(0, total - dp);
  const lines = [];

  if (dp > 0) {
    lines.push(line("dp", "Down payment now", dp, "normal"));
  } else {
    lines.push(line("dp", "Down payment", 0, "muted", "Waived"));
  }

  const minFootnote =
    min > 0 ? `At least ${formatPesos(min)} collected each visit.` : null;

  if (payments > 0) {
    // Declared installments for the balance AFTER the down payment. The
    // down payment is collected separately; the balance splits evenly
    // into N. Mirrors the DB's remaining / (N − installments-paid).
    if (remaining <= 0) {
      // The down payment already covers the whole total.
      return { ready: true, footnote: minFootnote, lines };
    }
    const parts = splitEven(remaining, payments);
    const equal = parts.every((a) => a === parts[0]);
    const label =
      dp > 0
        ? `Then, ${payments} payment${payments === 1 ? "" : "s"}`
        : `${payments} payment${payments === 1 ? "" : "s"}`;
    lines.push(line("spread", label, parts[0], "accent"));
    const roundNote = !equal ? `Last payment ${formatPesos(parts[parts.length - 1])}.` : null;
    return {
      ready: true,
      footnote: [minFootnote, roundNote].filter(Boolean).join(" ") || null,
      lines,
    };
  }

  // No horizon → open-ended Flexible: pay what you can each visit, clear
  // the rest on the final visit.
  lines.push(line("flex", "Then, flexible each visit", null, "muted", "any amount"));
  lines.push(line("final", "Remaining balance due on final visit", remaining, "accent"));
  return { ready: true, footnote: minFootnote, lines };
}
